import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {GenericErrorAlert} from "../../../components/Alert"
import {Loading} from "../../../components/Loading"
import {TReachRange, useReachActivitiesQuery} from "../../../queries/reachEngine"
import {AChannelsStatusEnum} from "../../../services/types.generated"
import {
  buildChannelRows,
  CHANNEL_ICONS,
  sortChannelsForTable,
  sumChannels,
  TReachChannelRow,
  useChannelLabels,
} from "../channels"
import {DashboardCard} from "./DashboardCard"

const STATUS_BADGE_COLORS: Record<AChannelsStatusEnum, string> = {
  [AChannelsStatusEnum.Active]: "bg-cr-green-pale text-cr-green-dark",
  [AChannelsStatusEnum.ActiveNoActivity]: "bg-cr-yellow-light text-cr-grey-80",
  [AChannelsStatusEnum.NotInFlows]: "bg-cr-slate-6 text-cr-slate-60",
}

const HEADER_CELL =
  "bg-cr-blue-super-light px-[10px] py-[9px] text-[10px] font-bold tracking-[0.8px] text-cr-slate-60 uppercase"
const NUMBER_CELL = "px-[10px] text-right tabular-nums whitespace-nowrap"

const StatusBadge: React.FC<{status: AChannelsStatusEnum}> = ({status}) => {
  const {t} = useTranslation()
  const label = {
    [AChannelsStatusEnum.Active]: t("ReachEngine_ChannelStatus_Active"),
    [AChannelsStatusEnum.ActiveNoActivity]: t("ReachEngine_ChannelStatus_NoActivityInPeriod"),
    [AChannelsStatusEnum.NotInFlows]: t("ReachEngine_ChannelStatus_NotInFlows"),
  }[status]

  return (
    <span
      className={twMerge(
        "inline-block rounded-full px-[8px] py-[2px] text-[9.5px] font-bold tracking-[0.6px] whitespace-nowrap uppercase",
        STATUS_BADGE_COLORS[status]
      )}
    >
      {label}
    </span>
  )
}

const NumberCell: React.FC<{value: number; notApplicable: boolean}> = ({value, notApplicable}) => {
  const {t} = useTranslation()

  return notApplicable ? (
    <td className={twMerge(NUMBER_CELL, "py-[12px] text-cr-slate-30")}>
      <span aria-hidden>—</span>
      <span className={"sr-only"}>{t("ReachEngine_ChannelsByResponses_NotApplicable")}</span>
    </td>
  ) : (
    <td className={twMerge(NUMBER_CELL, "py-[12px] text-cr-slate-90")}>{value.toLocaleString()}</td>
  )
}

const ChannelRow: React.FC<{row: TReachChannelRow}> = ({row}) => {
  const labels = useChannelLabels()
  const Icon = CHANNEL_ICONS[row.channel]
  const notInFlows = row.status === AChannelsStatusEnum.NotInFlows

  return (
    <tr data-channel={row.channel} className={"border-b border-cr-slate-2"}>
      <th scope={"row"} className={"px-[10px] py-[12px] text-left font-semibold whitespace-nowrap text-cr-slate-90"}>
        <span className={"flex items-center gap-2"}>
          <Icon aria-hidden className={"size-4 shrink-0 text-cr-slate-60"} />
          {labels[row.channel]}
        </span>
      </th>
      <td className={"px-[10px] py-[12px]"}>
        <StatusBadge status={row.status} />
      </td>
      <NumberCell value={row.activities} notApplicable={notInFlows} />
      <NumberCell value={row.responses} notApplicable={notInFlows} />
    </tr>
  )
}

const ChannelsByResponsesTable: React.FC<{
  rows: readonly TReachChannelRow[]
  totals: {activities: number; responses: number}
}> = ({rows, totals}) => {
  const {t} = useTranslation()

  return (
    <div
      role={"region"}
      aria-label={t("ReachEngine_ChannelsByResponses_Title")}
      tabIndex={0}
      className={
        "overflow-x-auto rounded-lg focus-visible:ring-2 focus-visible:ring-cr-blue focus-visible:outline-hidden"
      }
      data-testid={"channels-by-responses-table"}
    >
      <table className={"w-full min-w-[34rem] border-collapse text-[12.5px]"}>
        <caption className={"sr-only"}>{t("ReachEngine_ChannelsByResponses_Title")}</caption>
        <thead>
          <tr className={"border-b border-cr-blue-light"}>
            <th scope={"col"} className={twMerge(HEADER_CELL, "text-left")}>
              {t("ReachEngine_ChannelsByResponses_Column_Channel")}
            </th>
            <th scope={"col"} className={twMerge(HEADER_CELL, "text-left")}>
              {t("ReachEngine_ChannelsByResponses_Column_Status")}
            </th>
            <th scope={"col"} className={twMerge(HEADER_CELL, "text-right")}>
              {t("ReachEngine_ChannelsByResponses_Column_Activities")}
            </th>
            <th scope={"col"} className={twMerge(HEADER_CELL, "text-right")}>
              {t("ReachEngine_ChannelsByResponses_Column_Responses")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <ChannelRow key={row.channel} row={row} />
          ))}
        </tbody>
        <tfoot className={"bg-cr-blue-super-light font-bold text-cr-slate-90"}>
          <tr>
            <th scope={"row"} className={"px-[10px] py-[12px] text-left whitespace-nowrap"}>
              {t("ReachEngine_ChannelsByResponses_AllChannels")}
            </th>
            <td className={"px-[10px] py-[12px]"} />
            <td className={twMerge(NUMBER_CELL, "py-[12px]")}>{totals.activities.toLocaleString()}</td>
            <td className={twMerge(NUMBER_CELL, "py-[12px]")}>{totals.responses.toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export const ChannelsByResponsesCard: React.FC<{range: TReachRange}> = ({range}) => {
  const {t} = useTranslation()
  const {data: channels, error, isLoading, refetch} = useReachActivitiesQuery(range)
  const rows = React.useMemo(
    () => (channels ? sortChannelsForTable(buildChannelRows(channels)) : undefined),
    [channels]
  )
  const totals = rows ? sumChannels(rows) : undefined

  return (
    <DashboardCard
      title={t("ReachEngine_ChannelsByResponses_Title")}
      subtitle={
        totals
          ? t("ReachEngine_ChannelsByResponses_Subtitle", {
              activities: t("ReachEngine_ChannelsByResponses_ActivityCount", {
                count: totals.activities,
                value: totals.activities.toLocaleString(),
              }),
              responses: t("ReachEngine_ChannelsByResponses_ResponseCount", {
                count: totals.responses,
                value: totals.responses.toLocaleString(),
              }),
            })
          : undefined
      }
    >
      <div className={"mt-[16px]"}>
        {isLoading ? (
          <div className={"h-60"}>
            <Loading size={"xl"} />
          </div>
        ) : error || !rows || !totals ? (
          <GenericErrorAlert retry={refetch} />
        ) : (
          <ChannelsByResponsesTable rows={rows} totals={totals} />
        )}
      </div>
    </DashboardCard>
  )
}
